import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * The tenant and environment the operator is viewing (design 12.2: "tenant and environment
 * switcher").
 *
 * Important honesty boundary (NFR-6 AC1, FR-12): the TENANT the operator is authorised for comes
 * only from the verified Access identity on the BFF — the SPA never chooses the tenant it has rights
 * to. This switcher is a VIEW selector among the tenants the operator may already see (an admin
 * spanning several); it is a request parameter, never a grant. The server enforces tenancy on every
 * call regardless of what this control shows. The list of selectable tenants is therefore seeded
 * from the resolved identity (empty/unknown until the identity bootstrap of a later task fills it);
 * the shell does not fabricate one.
 *
 * The ENVIRONMENT (production / staging / local) selects which deployment's data to look at. The
 * BFF strips any client-supplied `x-oathera-env` (design 12.5), so this too is a view hint the
 * server resolves authoritatively, not something the client asserts to gain access.
 */

export type Environment = "production" | "staging" | "local";

export const ENVIRONMENTS: readonly Environment[] = ["production", "staging", "local"];

/** A selectable team: its id (submitted to the server) and the org display name (shown to the user). */
export interface TeamOption {
  readonly id: string;
  /** The organization display name; falls back to the id when the server provides no name. */
  readonly name: string;
}

export interface TenantEnvState {
  /** The currently selected team id for the view, or null when none is known yet. */
  readonly tenant: string | null;
  /** The team ids the operator may view (seeded from identity; may be a single team). */
  readonly availableTenants: readonly string[];
  /** The selectable teams as id+display-name options (what the switcher renders). */
  readonly availableTeams: readonly TeamOption[];
  /** The selected environment. */
  readonly environment: Environment;
}

export interface TenantEnvApi extends TenantEnvState {
  /** Select a tenant from `availableTenants`. Selecting an unknown tenant is ignored. */
  setTenant(tenant: string): void;
  setEnvironment(environment: Environment): void;
}

const TenantEnvContext = createContext<TenantEnvApi | null>(null);

export function TenantEnvProvider(props: {
  children: ReactNode;
  /**
   * The teams the operator may view (from the identity bootstrap), as id+display-name options;
   * default empty = unknown. The switcher shows `name` and submits `id`.
   */
  availableTeams?: readonly TeamOption[];
  initialEnvironment?: Environment;
}): ReactNode {
  const availableTeams = props.availableTeams ?? [];
  const availableTenants = useMemo(() => availableTeams.map((t) => t.id), [availableTeams]);
  const [tenant, setTenantState] = useState<string | null>(availableTeams[0]?.id ?? null);
  const [environment, setEnvironment] = useState<Environment>(
    props.initialEnvironment ?? "production",
  );

  const setTenant = useCallback<TenantEnvApi["setTenant"]>(
    (next) => {
      if (availableTenants.includes(next)) setTenantState(next);
    },
    [availableTenants],
  );

  const value = useMemo<TenantEnvApi>(
    () => ({ tenant, availableTenants, availableTeams, environment, setTenant, setEnvironment }),
    [tenant, availableTenants, availableTeams, environment, setTenant],
  );

  return <TenantEnvContext.Provider value={value}>{props.children}</TenantEnvContext.Provider>;
}

export function useTenantEnv(): TenantEnvApi {
  const ctx = useContext(TenantEnvContext);
  if (ctx === null) throw new Error("useTenantEnv must be used within <TenantEnvProvider>");
  return ctx;
}
